import { Trash2 } from "lucide-react";
import { memo, useMemo } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

import type { IShareBulkActionBarProps } from "./types";

const ShareBulkActionBarComponent = ({
  selectedCount,
  totalSelectableCount,
  isAllSelected,
  isBulkPending,
  onSelectAll,
  onDeselectAll,
  onBulkRemove,
  labels,
}: IShareBulkActionBarProps): JSX.Element | null => {
  const checkedState = useMemo<boolean | "indeterminate">(() => {
    if (isAllSelected) {
      return true;
    }
    if (selectedCount > 0) {
      return "indeterminate";
    }
    return false;
  }, [isAllSelected, selectedCount]);

  if (totalSelectableCount === 0) {
    return null;
  }

  const isDisabled = selectedCount === 0 || isBulkPending;

  const label =
    selectedCount === 0
      ? labels.selectAllLabel
      : labels.formatSelected(selectedCount);

  return (
    <div
      className="mb-2 flex h-10 items-center gap-3 rounded-md border border-border bg-accent/40 px-3"
      data-testid="share-bulk-action-bar"
    >
      <Checkbox
        checked={checkedState}
        onCheckedChange={(checked) =>
          checked ? onSelectAll() : onDeselectAll()
        }
        disabled={isBulkPending}
        aria-label={
          isAllSelected ? labels.deselectAllAria : labels.selectAllAria
        }
        data-testid="share-bulk-select-all"
      />
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Button
        variant="ghost"
        size="sm"
        className="ms-auto h-8 gap-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 hover:text-destructive active:bg-destructive/15"
        onClick={onBulkRemove}
        disabled={isDisabled}
        data-testid="share-bulk-remove"
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
        {labels.removeLabel}
      </Button>
    </div>
  );
};

export const ShareBulkActionBar = memo(ShareBulkActionBarComponent);

ShareBulkActionBar.displayName = "ShareBulkActionBar";
